import React, { useEffect } from 'react';

import { SquarespaceBriefFields } from '__generated__/SquarespaceBriefFields';
import { SquarespaceAssets } from '__generated__/types';
import SquarespaceNeedsField from 'projects/components/BriefFields/SquarespaceNeedsField';
import ContentSetting from 'projects/components/ContentSetting';
import { formatAndLinkify } from 'projects/components/FormatAndLinkify';
import { linkify } from 'projects/components/Linkify';
import SummaryCard from 'projects/components/summary/SummaryCard';
import SummaryCardItem from 'projects/components/summary/SummaryCardItem';
import { trackViewedBriefEvent } from 'projects/events';
import { routes } from 'projects/routes';
import { fieldTitles } from './DetailsPage';
import { getIndustry } from './industry';

const assets = (assets: SquarespaceAssets[]) => {
    return assets.map(asset => {
        switch (asset) {
            case SquarespaceAssets.IMAGES_VIDEO:
                return <div>{__('Image or Video')}</div>;
            case SquarespaceAssets.COPY:
                return <div>{__('Copy')}</div>;
            case SquarespaceAssets.LOGO:
                return <div>{__('Logo')}</div>;
            default:
                return <div>{asset}</div>;
        }
    });
};

const timeline = (timeline: any) => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return new Intl.DateTimeFormat('en-US', options).format(new Date(timeline));
};

interface Props {
    brief: {
        id: string;
        fields: SquarespaceBriefFields;
    };
}

function SummaryPage({ brief }: Props) {
    useEffect(() => {
        trackViewedBriefEvent({
            id: brief.id,
            page: 'Summary',
        });
    }, []);

    const { fields } = brief;
    return (
        <ContentSetting>
            <SummaryCard
                heading={__('Tell us about your Squarespace project')}
                url={routes.brief(brief, 1)}
            >
                <SummaryCardItem title={fieldTitles.title}>{fields.title}</SummaryCardItem>

                {fields.industry && (
                    <SummaryCardItem title={fieldTitles.industry}>
                        {getIndustry(fields.industry)}
                    </SummaryCardItem>
                )}
                {fields.url && (
                    <SummaryCardItem title={fieldTitles.url}>
                        {linkify([fields.url])}
                    </SummaryCardItem>
                )}
                {fields.needs && (
                    <SummaryCardItem title={fieldTitles.needs}>
                        {SquarespaceNeedsField.labelFor(fields.needs)}
                    </SummaryCardItem>
                )}
                {fields.description && (
                    <SummaryCardItem title={fieldTitles.description}>
                        {formatAndLinkify(fields.description)}
                    </SummaryCardItem>
                )}
                {fields.assets.length > 0 && (
                    <SummaryCardItem title={fieldTitles.assets}>
                        {assets(fields.assets)}
                    </SummaryCardItem>
                )}
                {fields.inspirationUrls.length > 0 && (
                    <SummaryCardItem title={fieldTitles.inspirationUrls}>
                        {linkify(fields.inspirationUrls)}
                    </SummaryCardItem>
                )}
                <SummaryCardItem title={fieldTitles.timeline}>
                    {fields.timeline ? timeline(fields.timeline) : __('No')}
                </SummaryCardItem>
            </SummaryCard>
        </ContentSetting>
    );
}

export default SummaryPage;
